import { type SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { useSaveBeforeUnload } from "./useSaveBeforeUnload";

// These editors replace a stable record ID. Serialize writes and retain the newest
// draft when an earlier acknowledgement refreshes the workspace underneath it.
export function useAutosavedDraft<T>({
  scope,
  source,
  writable,
  isValid = () => true,
  onSave,
  onSaved,
}: {
  scope: string;
  source: T;
  writable: boolean;
  isValid?: (value: T) => boolean;
  onSave: (value: T) => unknown;
  onSaved?: () => void;
}) {
  const sourceSignature = JSON.stringify(source);
  const [value, setValue] = useState(source);
  const [savedSignature, setSavedSignature] = useState(sourceSignature);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const valueRef = useRef(source);
  const savedRef = useRef(sourceSignature);
  const sourceRef = useRef(sourceSignature);
  const incoming = useRef(source);
  incoming.current = source;
  const scopeRef = useRef(scope);
  const workingRef = useRef(false);
  const failedRef = useRef(false);
  const options = useRef({ scope, writable, isValid, onSave, onSaved });
  options.current = { scope, writable, isValid, onSave, onSaved };
  const signature = JSON.stringify(value);
  const valid = isValid(value);
  const dirty = signature !== savedSignature;
  const saving = working || (dirty && writable && valid && !error);

  useEffect(() => {
    const changedScope = scopeRef.current !== scope;
    const clean = !workingRef.current && JSON.stringify(valueRef.current) === savedRef.current;
    if (changedScope || (sourceRef.current !== sourceSignature && clean && sourceSignature !== savedRef.current)) {
      scopeRef.current = scope;
      valueRef.current = incoming.current;
      savedRef.current = sourceSignature;
      failedRef.current = false;
      setValue(incoming.current);
      setSavedSignature(sourceSignature);
      setError("");
    }
    sourceRef.current = sourceSignature;
  }, [scope, sourceSignature]);

  const save = useCallback(async () => {
    if (workingRef.current || failedRef.current) return;
    const savingScope = scopeRef.current;
    workingRef.current = true;
    setWorking(true);
    try {
      while (
        scopeRef.current === savingScope &&
        options.current.scope === savingScope &&
        options.current.writable &&
        options.current.isValid(valueRef.current) &&
        JSON.stringify(valueRef.current) !== savedRef.current
      ) {
        const pending = valueRef.current;
        const pendingSignature = JSON.stringify(pending);
        await options.current.onSave(pending);
        if (scopeRef.current !== savingScope || options.current.scope !== savingScope) return;
        savedRef.current = pendingSignature;
        setSavedSignature(pendingSignature);
        if (JSON.stringify(valueRef.current) === pendingSignature) options.current.onSaved?.();
      }
    } catch (caught) {
      if (scopeRef.current === savingScope) {
        failedRef.current = true;
        // Keep even non-Error failures paused until an explicit retry.
        setError(caught instanceof Error && caught.message ? caught.message : "save-failed");
      }
    } finally {
      workingRef.current = false;
      setWorking(false);
    }
  }, []);

  useEffect(() => {
    if (
      !writable ||
      working ||
      scopeRef.current !== scope ||
      signature !== JSON.stringify(valueRef.current) ||
      failedRef.current ||
      !valid ||
      signature === savedRef.current
    )
      return;
    const timer = window.setTimeout(() => void save(), 250);
    return () => window.clearTimeout(timer);
  }, [scope, signature, valid, writable, working, save]);

  useSaveBeforeUnload(dirty || working);

  function update(next: SetStateAction<T>) {
    if (!options.current.writable) return;
    valueRef.current = typeof next === "function" ? (next as (current: T) => T)(valueRef.current) : next;
    setValue(valueRef.current);
  }

  function retry() {
    if (!options.current.writable) return;
    failedRef.current = false;
    setError("");
    void save();
  }

  return { value, update, dirty, saving, error, retry };
}
